import { useCallback, useEffect, useState } from 'react';
import type { OddsFormat } from '@hienbet/shared';
import {
  api,
  captureTokenFromUrl,
  formatChips,
  loginUrl,
  readOddsFormat,
  writeOddsFormat,
  type Me,
} from './api.js';
import { useLive } from './hooks/useLive.js';
import { BookPage } from './pages/Book.js';
import { LedgerPage } from './pages/Ledger.js';
import { LeaderboardPage } from './pages/Leaderboard.js';
import { HistoryPage } from './pages/History.js';
import { RewardsPage } from './pages/Rewards.js';
import { QueuePage } from './pages/Queue.js';
import { ResultsPage } from './pages/Results.js';
import { Logo } from './components/Logo.js';

type Route = 'book' | 'ledger' | 'leaderboard' | 'history' | 'rewards' | 'queue' | 'results';

function routeFromPath(path: string): Route {
  if (path.startsWith('/ledger')) return 'ledger';
  if (path.startsWith('/leaderboard')) return 'leaderboard';
  if (path.startsWith('/history')) return 'history';
  if (path.startsWith('/rewards')) return 'rewards';
  if (path.startsWith('/queue')) return 'queue';
  if (path.startsWith('/results')) return 'results';
  return 'book';
}

export function App() {
  const [route, setRoute] = useState<Route>(() => routeFromPath(location.pathname));
  const [me, setMe] = useState<Me | null>(null);
  const [oddsFormat, setOddsFormat] = useState<OddsFormat>(() => readOddsFormat());
  const [claiming, setClaiming] = useState(false);
  const live = useLive();

  const chooseFormat = (format: OddsFormat) => {
    writeOddsFormat(format);
    setOddsFormat(format);
  };

  const refreshMe = useCallback(async () => {
    try {
      setMe(await api.me());
    } catch {
      setMe({ loggedIn: false });
    }
  }, []);

  useEffect(() => {
    // Collect the session the server left in the fragment after login, before
    // asking who we are.
    captureTokenFromUrl();
    void refreshMe();
  }, [refreshMe]);

  // Balance updates arrive over the live socket; keep the header in step.
  useEffect(() => {
    if (live.balance !== null) {
      setMe((prev) => (prev?.loggedIn ? { ...prev, balance: live.balance ?? prev.balance } : prev));
    }
  }, [live.balance]);

  useEffect(() => {
    const onPop = () => setRoute(routeFromPath(location.pathname));
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, []);

  const navigate = (next: Route, path: string) => {
    history.pushState({}, '', path);
    setRoute(next);
  };

  return (
    <div className="shell">
      <header className="topbar">
        <a
          className="wordmark"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('book', '/');
          }}
        >
          <Logo size={22} />
          <span className="wordmark-text">HIENBET</span>
        </a>

        <nav className="nav">
          {(
            [
              ['book', '/', 'Book'],
              ['results', '/results', 'Results'],
              ['leaderboard', '/leaderboard', 'Leaderboard'],
              ['rewards', '/rewards', 'Spend'],
              ['history', '/history', 'My bets'],
              // The Ledger still lives at /ledger; it earns a nav slot back if
              // it earns the traffic. Only Hien ever sees Queue.
              ...(me?.isBroadcaster ? ([['queue', '/queue', 'Queue']] as const) : []),
            ] as const
          ).map(([key, path, label]) => (
            <a
              key={key}
              href={path}
              className={route === key ? 'on' : ''}
              onClick={(e) => {
                e.preventDefault();
                navigate(key, path);
              }}
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="topbar-right">
          {live.connected ? null : <span className="offline-pip" title="Reconnecting">offline</span>}
          {me?.loggedIn ? (
            <>
              {me.dailyClaim?.available && (
                <button
                  className="claim"
                  disabled={claiming}
                  onClick={async () => {
                    setClaiming(true);
                    try {
                      await api.claimDaily();
                      await refreshMe();
                    } catch {
                      /* already claimed elsewhere; the refresh will correct it */
                      await refreshMe();
                    } finally {
                      setClaiming(false);
                    }
                  }}
                >
                  {claiming ? '…' : `Claim ${formatChips(me.dailyClaim.amount)}`}
                </button>
              )}
              <span className="balance num">{formatChips(me.balance ?? 0)}</span>
              <img className="avatar" src={me.user?.avatarUrl} alt="" />
              <button
                className="ghost"
                onClick={async () => {
                  await api.logout();
                  void refreshMe();
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <a className="login" href={loginUrl()}>
              Log in with Twitch
            </a>
          )}
        </div>
      </header>

      <main>
        {route === 'book' && (
          <BookPage live={live} me={me} onBalance={refreshMe} oddsFormat={oddsFormat} />
        )}
        {route === 'ledger' && <LedgerPage />}
        {route === 'results' && <ResultsPage />}
        {route === 'leaderboard' && <LeaderboardPage live={live} />}
        {route === 'rewards' && <RewardsPage me={me} onBalance={refreshMe} />}
        {route === 'queue' && <QueuePage me={me} />}
        {route === 'history' && <HistoryPage me={me} oddsFormat={oddsFormat} />}
      </main>

      <footer className="footer">
        <span>
          Play money. Chips have no value, cannot be cashed out, and cannot be exchanged for
          anything.
        </span>
        {live.streamDelaySeconds !== null && (
          <span className="footer-delay">
            Board runs {live.streamDelaySeconds}s behind real time, matched to the stream.
          </span>
        )}
        <span className="oddsformat">
          <button
            className={oddsFormat === 'american' ? 'on' : ''}
            onClick={() => chooseFormat('american')}
          >
            −110
          </button>
          <button
            className={oddsFormat === 'decimal' ? 'on' : ''}
            onClick={() => chooseFormat('decimal')}
          >
            1.91
          </button>
        </span>
      </footer>
    </div>
  );
}
